import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArchiveRestore, ChevronDown, ChevronRight, FileSpreadsheet, Trash2, Undo2 } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { submitErrorMessage } from '@/lib/formErrors';
import { DATE_MSG } from '@/lib/submissions';

export interface MassChangeUpload {
  UploadID: number; Kind: 'Client' | 'Engagement'; FileName: string; FileSize: number | null;
  RowsInFile: number | null; RequestsCreated: number | null; RequestsFailed: number | null;
  UploadedBy: string; UploadedByEmail: string; UploadedDateUtc: string;
  Mode: 'File' | 'PendingRequests'; RowsChanged: number;
  MaconomyStatus: string | null; ConstantStatus: string | null; DateCompleted: string | null;
  ChecklistUpdatedBy: string | null; IsCompleted: boolean; CompletedBy: string | null; CompletedUtc: string | null;
}
interface UploadRow {
  RowNumber: number | null; RecordKey: string; RecordLabel: string | null; Status: 'Created' | 'Failed' | 'Submitted'; Message: string | null;
  Changes: { field: string; from: string; to: string }[];
}

export const massUploadsKey = ['mass-change-uploads'] as const;

/** Mass Change requests: the user's own (Mass Change page) or everyone's (admins, Submitted Forms).
 *  status (admins): '' open | 'only' completed | 'all'. */
export function useMassChangeUploads(scope: 'all' | 'mine', kind?: 'Client' | 'Engagement', status = '') {
  return useQuery({
    queryKey: [...massUploadsKey, scope, kind ?? '', status],
    queryFn: () => api.get<MassChangeUpload[]>(`/mass-change/uploads?scope=${scope}${kind ? `&kind=${kind}` : ''}${status ? `&status=${status}` : ''}`),
    staleTime: 30_000,
  });
}

// Times are stored in UTC; show Eastern time for everyone.
const when = (s: string | null) => (s ? new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'short' }) : '');
const day = (s: string | null) => (s ? new Date(s).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : '');
const iso = (s: string | null) => (s ? String(s).slice(0, 10) : '');
const OPTIONS = ['', 'Yes', 'No', 'N/A'];
const DONE = ['Yes', 'N/A'];
type Edit = { maconomy?: string; constant?: string; date?: string };

/** Status shown to the person who submitted it. */
function statusOf(u: MassChangeUpload) {
  if (u.Mode !== 'File') return `${u.RequestsCreated ?? 0} requests sent to Pending Requests${u.RequestsFailed ? ` (${u.RequestsFailed} failed)` : ''}`;
  return u.IsCompleted ? `Completed ${day(u.CompletedUtc)}` : 'Open';
}

/** The changed clients / engagements of one request (old -> new). */
function ChangesList({ id }: { id: number }) {
  const detail = useQuery({
    queryKey: [...massUploadsKey, 'detail', id],
    queryFn: () => api.get<MassChangeUpload & { rows: UploadRow[] }>(`/mass-change/uploads/${id}`),
  });
  if (detail.isFetching && !detail.data) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <ul className="max-h-96 space-y-1.5 overflow-auto">
      {(detail.data?.rows ?? []).map((r, i) => (
        <li key={i}>
          <span className="font-medium">{r.RecordKey}</span>{r.RecordLabel ? ` · ${r.RecordLabel}` : ''}
          <span className="text-xs text-muted-foreground"> (row {r.RowNumber})</span>
          {r.Status === 'Failed' && <span className="ml-2 text-xs font-medium text-destructive">Not created: {r.Message}</span>}
          {r.Status === 'Created' && <span className="ml-2 text-xs text-green-700">Request created</span>}
          <ul className="ml-5 list-disc text-muted-foreground">
            {r.Changes.map((c, j) => (
              <li key={j}><span className="text-foreground">{c.field}:</span> {c.from || '(blank)'} → <span className="font-medium text-foreground">{c.to || '(blank)'}</span></li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/**
 * Mass Change requests.
 *  - scope 'mine' (Mass Change page): the user's own requests with their status.
 *  - scope 'all' (Submitted Forms > Mass Change, admins): worked like the other submitted
 *    forms. Download the file, import it into Maconomy, set Maconomy (and Constant for client
 *    files) and the Date Completed, then Submit; a finished request moves to Completed and
 *    the submitter is emailed. Reopen and Delete as on the other tabs.
 */
export function MassChangeUploads({ scope, kind, emptyText, status = '' }: {
  scope: 'all' | 'mine'; kind?: 'Client' | 'Engagement'; emptyText?: string; status?: string;
}) {
  const notify = useToast();
  const qc = useQueryClient();
  const q = useMassChangeUploads(scope, kind, scope === 'all' ? status : '');
  const [open, setOpen] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<number, Edit>>({});
  const [confirm, setConfirm] = useState<{ kind: 'submit' } | { kind: 'delete'; u: MassChangeUpload } | null>(null);
  const [busy, setBusy] = useState(false);
  const all = scope === 'all';
  const rows = q.data ?? [];

  const val = (u: MassChangeUpload, k: keyof Edit) =>
    edits[u.UploadID]?.[k] ?? (k === 'maconomy' ? u.MaconomyStatus ?? '' : k === 'constant' ? u.ConstantStatus ?? '' : iso(u.DateCompleted));
  // Items that must be Yes / N/A before the Date Completed counts (like Pending).
  const itemsDone = (u: MassChangeUpload, e: Edit = {}) => {
    const v = (k: keyof Edit) => e[k] ?? val(u, k);
    return DONE.includes(v('maconomy')) && (u.Kind !== 'Client' || DONE.includes(v('constant')));
  };
  const setVal = (u: MassChangeUpload, k: keyof Edit, v: string) => setEdits((s) => {
    const next = { ...s[u.UploadID], [k]: v };
    if (k !== 'date' && !itemsDone(u, next)) next.date = '';
    return { ...s, [u.UploadID]: next };
  });
  const needsDate = (u: MassChangeUpload) => itemsDone(u) && !val(u, 'date');
  const editedRows = rows.filter((u) => edits[u.UploadID] && u.Mode === 'File' && !u.IsCompleted);
  const blocked = editedRows.some(needsDate);
  const refresh = () => qc.invalidateQueries({ queryKey: massUploadsKey });

  async function download(u: MassChangeUpload) {
    try { await api.download(`/mass-change/uploads/${u.UploadID}/file`, u.FileName); }
    catch (e) { notify('error', submitErrorMessage(e)); }
  }

  async function act(fn: () => Promise<unknown>, ok: (r: unknown) => string) {
    setBusy(true);
    try { const r = await fn(); notify('success', ok(r)); await refresh(); }
    catch (e) { notify('error', e instanceof ApiError ? e.problem.title : submitErrorMessage(e)); }
    finally { setBusy(false); }
  }

  const submit = () => {
    setConfirm(null);
    const body = editedRows.map((u) => ({
      uploadId: u.UploadID, maconomy: val(u, 'maconomy') || null,
      constant: u.Kind === 'Client' ? val(u, 'constant') || null : null, dateCompleted: val(u, 'date') || null,
    }));
    if (!body.length) return;
    return act(async () => { const r = await api.put<{ completed: number[] }>('/mass-change/uploads/checklist', { rows: body }); setEdits({}); return r; },
      (r) => {
        const done = (r as { completed?: number[] })?.completed ?? [];
        return `Saved ${body.length} request${body.length === 1 ? '' : 's'}.` + (done.length
          ? ` #${done.join(', #')} moved to Completed and the submitter was emailed.` : '');
      });
  };

  if (q.isLoading) return <p className="p-3 text-sm text-muted-foreground">Loading mass change requests…</p>;

  if (!all) {
    if (!rows.length) return <p className="text-sm text-muted-foreground">{emptyText ?? 'No mass change requests yet.'}</p>;
    return (
      <table className="w-full text-sm">
        <thead className="table-head">
          <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
            <th className="px-3 py-2">#</th><th className="px-3 py-2">File</th><th className="px-3 py-2">Submitted</th>
            <th className="px-3 py-2">Rows changed</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <Fragment key={u.UploadID}>
              <tr className="border-b border-border/60 align-top">
                <td className="px-3 py-2 font-medium">{u.UploadID}</td>
                <td className="px-3 py-2">
                  <button type="button" className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => void download(u)}
                    title="Download the file that was submitted">
                    <FileSpreadsheet className="h-4 w-4 shrink-0" /> {u.FileName}
                  </button>
                </td>
                <td className="whitespace-nowrap px-3 py-2">{when(u.UploadedDateUtc)}</td>
                <td className="px-3 py-2">{u.RowsChanged}</td>
                <td className="px-3 py-2">
                  {u.Mode === 'File' && u.IsCompleted
                    ? <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">{statusOf(u)}</span>
                    : <span className="text-xs">{statusOf(u)}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button type="button" className="btn-secondary inline-flex items-center gap-1 !py-1 text-xs"
                    onClick={() => setOpen(open === u.UploadID ? null : u.UploadID)}>
                    {open === u.UploadID ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} View
                  </button>
                </td>
              </tr>
              {open === u.UploadID && (
                <tr className="border-b border-border/60 bg-secondary/30"><td colSpan={6} className="px-3 py-2"><ChangesList id={u.UploadID} /></td></tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-shrink-0 justify-end gap-2 border-b border-border p-2">
        <button className="btn-secondary inline-flex items-center gap-1" disabled={!editedRows.length || busy}
          title="Discard unsaved status / date edits" onClick={() => setEdits({})}>
          <Undo2 className="h-4 w-4" /> Discard Changes
        </button>
        <button className="btn-success" disabled={!editedRows.length || blocked || busy} title={blocked ? DATE_MSG : ''}
          onClick={() => setConfirm({ kind: 'submit' })}>
          {busy ? 'Submitting…' : 'Submit'}
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">#</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">File</th>
              <th className="px-3 py-2">Submitted</th><th className="px-3 py-2">Rows</th>
              <th className="px-2 py-2">Maconomy</th><th className="px-2 py-2">Constant</th><th className="px-2 py-2">Date Completed</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-4 text-muted-foreground">{emptyText ?? 'No mass change requests match.'}</td></tr>
            )}
            {rows.map((u) => {
              const worked = u.Mode === 'File';
              const locked = !worked || u.IsCompleted || busy;
              const ready = itemsDone(u);
              return (
                <Fragment key={u.UploadID}>
                  <tr className={`border-b border-border/60 align-top ${edits[u.UploadID] ? 'bg-warning/10' : ''}`}>
                    <td className="px-3 py-2 font-semibold">{u.UploadID}</td>
                    <td className="px-3 py-2">
                      {u.Kind === 'Client' ? 'Clients' : 'Engagements'}
                      {worked && u.IsCompleted && (
                        <div className="mt-1 text-xs">
                          <span className="rounded bg-green-100 px-1.5 py-0.5 font-medium text-green-800">Completed</span>
                          <div className="mt-0.5 text-muted-foreground">{day(u.CompletedUtc)}{u.CompletedBy && ` by ${u.CompletedBy}`}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button type="button" className="inline-flex items-center gap-1 text-left text-primary hover:underline" onClick={() => void download(u)}
                        title="Download the file to import into Maconomy">
                        <FileSpreadsheet className="h-4 w-4 shrink-0" /> {u.FileName}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {day(u.UploadedDateUtc)}
                      <div className="text-xs text-muted-foreground">by {u.UploadedBy}</div>
                    </td>
                    <td className="px-3 py-2">{u.RowsChanged}</td>
                    {worked ? (
                      <>
                        <td className="px-2 py-1.5">
                          <select className="input w-[4.75rem] px-2 py-1" disabled={locked} value={val(u, 'maconomy')}
                            onChange={(e) => setVal(u, 'maconomy', e.target.value)}>
                            {OPTIONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          {u.Kind === 'Client' ? (
                            <select className="input w-[4.75rem] px-2 py-1" disabled={locked} value={val(u, 'constant')}
                              onChange={(e) => setVal(u, 'constant', e.target.value)}>
                              {OPTIONS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                            </select>
                          ) : <span className="text-xs text-muted-foreground">n/a</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <input type="date" className="input w-[8.5rem] px-2 py-1" disabled={locked || !ready}
                              title={!ready ? (u.Kind === 'Client' ? 'Set Maconomy and Constant to Yes or N/A first' : 'Set Maconomy to Yes or N/A first') : ''}
                              value={ready ? val(u, 'date') : ''} onChange={(e) => setVal(u, 'date', e.target.value)} />
                            {!locked && needsDate(u) && <span title={DATE_MSG} className="font-bold text-orange-500">!</span>}
                          </div>
                        </td>
                      </>
                    ) : (
                      <td colSpan={3} className="px-2 py-2 text-xs text-muted-foreground">
                        Older upload: {u.RequestsCreated ?? 0} change requests were sent to Pending Requests.
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" className="btn-secondary inline-flex items-center gap-1 !py-1 text-xs"
                          onClick={() => setOpen(open === u.UploadID ? null : u.UploadID)}>
                          {open === u.UploadID ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Changes
                        </button>
                        {worked && u.IsCompleted && (
                          <button className="btn-secondary inline-flex items-center gap-1 !py-1 text-xs" disabled={busy}
                            title="Reopen: back to the open list so the checklist can be changed"
                            onClick={() => void act(() => api.post(`/mass-change/uploads/${u.UploadID}/reopen`, {}), () => `Request #${u.UploadID} reopened.`)}>
                            <ArchiveRestore className="h-3.5 w-3.5" /> Reopen
                          </button>
                        )}
                        <button className="text-destructive hover:opacity-70" title="Delete Request" disabled={busy}
                          onClick={() => setConfirm({ kind: 'delete', u })}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                  {open === u.UploadID && (
                    <tr className="border-b border-border/60 bg-secondary/30"><td colSpan={9} className="px-3 py-2"><ChangesList id={u.UploadID} /></td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={confirm !== null}
        message={confirm?.kind === 'delete'
          ? `Delete mass change request #${confirm.u.UploadID} (${confirm.u.FileName}, submitted by ${confirm.u.UploadedBy})? It will be removed from Submitted Forms. This cannot be undone from the app.`
          : 'Do you wish to submit these changes? Requests that are done will move to Completed and the submitter will be emailed.'}
        confirmLabel={confirm?.kind === 'delete' ? 'Delete' : 'Submit'}
        cancelLabel="Cancel"
        onConfirm={() => {
          const c = confirm;
          if (c?.kind === 'delete') {
            setConfirm(null);
            void act(() => api.del(`/mass-change/uploads/${c.u.UploadID}`), () => `Request #${c.u.UploadID} deleted.`);
          } else void submit();
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
